import React, { useState, useMemo, useEffect } from 'react';
import studentService from '../../services/api/student';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Search,
  Users,
  BookOpen,
  Star,
  History,
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  X,
  Zap,
  ArrowRight,
  MonitorPlay,
  Sparkles,
  Filter
} from 'lucide-react';

// APIs & Components
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';


// Search Hook
import { useGenericSearch } from '../../hooks/useSearch';
import StudentLessonCard from '../../components/cards/StudentLessonCard';

const toEnglishNumbers = (num) => {
  return String(num);
};

const Lessons = () => {
  // State
  const [ratingModal, setRatingModal] = useState({ 
    isOpen: false, 
    type: 'lesson', // 'lesson' or 'teacher'
    lessonId: null, 
    currentRating: 0,
    review: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [lessonsPerPage] = useState(6);

  // Filter states like teacher pages
  const [selectedCourse, setSelectedCourse] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hooks
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  // Navigation Handler
  const handleViewLesson = (lessonId) => {
    navigate(`/lessons/${lessonId}`);
  };

  // Data Queries
  const { data: subscriptions } = useQuery(
    'subscription-info',
    () => studentService.getSubscriptionInfo()
  );

  const { data: recentLessons } = useQuery(
    'recent-lessons',
    () => studentService.getRecentLessons().then(response => response.lessons || response.data?.lessons || [])
  );

  // Get filters data (student-specific)
  const { data: subjectsData } = useQuery(
    ['subjects-data', 'list'], 
    () => studentService.getStudentSubjects(),
    {
      retry: false,
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  const { data: coursesData } = useQuery(
    ['courses-data', 'list'], 
    () => studentService.getStudentCourses(),
    {
      retry: false,
      staleTime: 300000,
      refetchOnWindowFocus: false
    }
  );

  const { data: lessons, isLoading } = useQuery(
    ['student-lessons', selectedCourse, selectedSubject],
    () => studentService.getStudentLessons({ 
      course: selectedCourse,
      subject: selectedSubject
    })
  );

  // Ensure progress field is properly set in lessons
  const lessonsWithProgress = React.useMemo(() => {
    if (!lessons || lessons.length === 0) return [];
    return lessons.map(lesson => ({
      ...lesson,
      progress: lesson.completed ? 100 : (lesson.progress || 0)
    }));
  }, [lessons]);

  // Unified Search Logic using useGenericSearch
  const {
    searchTerm: hookSearchTerm,
    filteredData: searchedLessons,
    setSearchTerm: setHookSearchTerm
  } = useGenericSearch(lessonsWithProgress, ['title', 'description', 'content_type', 'course_title']);

  // Combine filters data
  React.useMemo(() => {
    return {
      subjects: Array.isArray(subjectsData) ? subjectsData : subjectsData?.subjects || [], // Handle both array and object formats
      courses: coursesData?.courses || [], // Extract courses from response object
    };
  }, [subjectsData, coursesData]);

  // Filter subjects (all student subjects)
  const filteredSubjects = useMemo(() => {
    return Array.isArray(subjectsData) ? subjectsData : subjectsData?.subjects || [];
  }, [subjectsData]);

  // Extract courses based on selected subject (from student courses)
  const allCourses = useMemo(() => {
    const subjectId = Number(selectedSubject);
    if (!coursesData?.courses) return [];
    
    if (!subjectId) return coursesData.courses;
    
    return coursesData.courses.filter(
      course => Number(course.teacher_subject_id) === subjectId
    );
  }, [coursesData, selectedSubject]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [hookSearchTerm, selectedCourse, selectedSubject]);

  // Helper Functions
  const getDaysRemaining = (endDate) => {
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getNearestExpiring = () => {
    if (!subscriptions?.data?.subscription || !Array.isArray(subscriptions?.data?.subscription)) {
      const altSub = subscriptions?.subscription || subscriptions?.data;
      if (Array.isArray(altSub)) {
        const sorted = [...altSub].sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
        return sorted[0];
      }
      if (altSub && altSub.end_date) return altSub;
      return null;
    }
    
    const subscriptionArray = Array.isArray(subscriptions.data.subscription) 
      ? subscriptions.data.subscription 
      : [subscriptions.data.subscription];
    
    if (subscriptionArray.length === 0) return null;
    const sorted = [...subscriptionArray].sort((a, b) => new Date(a.end_date) - new Date(b.end_date));
    return sorted[0];
  };

  const nearestExpiring = getNearestExpiring();

  // Rating Mutation
  const rateLessonMutation = useMutation(
    async ({ lessonId, rating, review }) => {
      const response = await studentService.rateLesson(lessonId, { rating, review });
      return response.data;
    },
    {
      onSuccess: (_data, { lessonId }) => {
        queryClient.invalidateQueries('student-lessons');
        queryClient.invalidateQueries('recent-lessons');
        if (lessonId) {
          queryClient.invalidateQueries(['lesson', lessonId]);
          queryClient.refetchQueries(['lesson', lessonId]);
        }
        toast.success('شكرًا لتقييمك! تم حفظه بنجاح');
        setRatingModal({ isOpen: false, type: 'lesson', lessonId: null, currentRating: 0, review: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل تسجيل التقييم');
      }
    }
  );

  // Event Handlers
  const handleRateLesson = (lessonId, currentRating) => {
    setRatingModal({ isOpen: true, type: 'lesson', lessonId, currentRating, review: '' });
  };

  const submitRating = () => {
    if (ratingModal.lessonId && ratingModal.currentRating > 0) {
      rateLessonMutation.mutate({
        lessonId: ratingModal.lessonId,
        rating: ratingModal.currentRating,
        review: ratingModal.review.trim()
      });
    }
  };

  const handleInputChange = (value) => {
    setHookSearchTerm(value);
  };

  // Pagination logic using unified search results
  const indexOfLastLesson = currentPage * lessonsPerPage;
  const indexOfFirstLesson = indexOfLastLesson - lessonsPerPage;
  const currentLessons = searchedLessons?.slice(indexOfFirstLesson, indexOfLastLesson);
  const totalPages = Math.ceil((searchedLessons?.length || 0) / lessonsPerPage);

  // Reset page when filters change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Filter UI Section
  const handleFilterChange = (filterName, value) => {
    switch (filterName) {
      case 'course':
        setSelectedCourse(value);
        break;
      case 'subject':
        setSelectedSubject(value);
        break;
      default:
        break;
    }
  };

  // Star Rating Component
  const StarRating = ({ rating, onRate, size = 'small' }) => {
    const starSize = size === 'large' ? 'w-10 h-10' : 'w-4 h-4';
    const stars = [1, 2, 3, 4, 5];
    
    return (
      <div className="flex items-center gap-1">
        {stars.map((star) => (
          <button
            key={star}
            onClick={() => onRate && onRate(star)}
            className={`${onRate ? 'cursor-pointer hover:scale-125' : 'cursor-default'} transition-all duration-200`}
            disabled={!onRate}
            type="button"
          >
            <Star
              className={`${starSize} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-black animate-pulse uppercase tracking-widest text-xs">
          جاري تجهيز دروسك المفضلة...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-10 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <MonitorPlay className="text-primary-600" size={32} />
              مكتبة دروسي
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
              <Zap size={18} className="text-amber-500" />
              استكمل رحلة تفوقك، كافة محتوياتك التعليمية في مكان واحد
            </p>
          </div>

          <div className="relative group w-full md:w-96">
            <Search className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن درس محدد في مكتبتك..."
              value={hookSearchTerm}
              onChange={(e) => handleInputChange(e.target.value)}
              className="w-full pr-14 pl-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />
          </div>
        </div>
      </div>

      {/* Subscription Expiry Alert Section */}
      {nearestExpiring && (
        <div className={`relative overflow-hidden rounded-[2rem] p-6 lg:p-8 border-l-[12px] transition-all duration-500 animate-in slide-in-from-top-4 shadow-2xl ${
          getDaysRemaining(nearestExpiring.end_date) <= 7 
            ? 'bg-red-50 border-red-500 text-red-900 shadow-red-100' 
            : 'bg-blue-50 border-blue-500 text-blue-900 shadow-blue-100'
        }`}>
          <div className="flex flex-col sm:flex-row items-center gap-6 relative z-10">
            <div className={`p-4 rounded-2xl ${getDaysRemaining(nearestExpiring.end_date) <= 7 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
              <AlertCircle size={32} className={getDaysRemaining(nearestExpiring.end_date) <= 7 ? 'animate-bounce' : ''} />
            </div>
            <div className="flex-1 text-center sm:text-right">
              <h4 className="font-black text-xl mb-1">تنبيه انتهاء الاشتراك</h4>
              <p className="font-medium opacity-80 leading-relaxed text-sm lg:text-base">
                اشتراك <span className="font-black underline">{nearestExpiring.subject_name}</span> مع <span className="font-black">أ / {nearestExpiring.teacher_name}</span> 
                {getDaysRemaining(nearestExpiring.end_date) <= 7 
                  ? ` سينتهي قريباً جداً خلال (${getDaysRemaining(nearestExpiring.end_date)}) أيام.`
                  : ` متبقي على انتهائه (${getDaysRemaining(nearestExpiring.end_date)}) يوماً.`
                }
              </p>
            </div>
            <button 
              onClick={() => navigate('/student/subscriptions')}
              className={`w-full sm:w-auto px-10 py-4 rounded-2xl font-black text-sm transition-all transform active:scale-95 shadow-xl ${
                getDaysRemaining(nearestExpiring.end_date) <= 7 
                  ? 'bg-red-600 text-white hover:bg-red-700 shadow-red-200' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200'
              }`}
            >
              تجديد الاشتراك الآن
            </button>
          </div>
          <Sparkles className="absolute top-4 left-4 opacity-10" size={64} />
        </div>
      )}

      {/* Recently Watched Section */}
      {recentLessons && recentLessons.length > 0 && (
        <div className="space-y-6">
          <div className="flex items-center gap-3 px-2">
            <div className="w-2 h-8 bg-primary-600 rounded-full"></div>
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2 tracking-tight">
              تابع من حيث توقفت
              <History className="text-primary-500 animate-spin-slow" size={20} />
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recentLessons.slice(0, 3).map((lesson, index) => (
              <div 
                key={`recent-${lesson.id}-${index}`} 
                onClick={() => handleViewLesson(lesson.id)}
                className="group bg-white border border-gray-100 rounded-[2.5rem] p-6 hover:shadow-2xl transition-all duration-500 cursor-pointer flex flex-col justify-between relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div>
                  <div className="flex items-start justify-between mb-4">
                    <span className="bg-primary-50 text-primary-700 text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-lg border border-primary-100">
                      {lesson.subject_name}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:text-primary-600 group-hover:bg-primary-50 transition-all">
                      <ChevronLeft size={20} className="group-hover:-translate-x-1 transition-transform"/>
                    </div>
                  </div>
                  <h3 className="font-black text-gray-900 text-lg mb-2 group-hover:text-primary-600 transition-colors leading-tight line-clamp-2">
                    {lesson.title}
                  </h3>
                  <p className="text-xs font-bold text-gray-400 flex items-center gap-1.5 mb-6 uppercase tracking-tighter">
                    <Users size={14} className="text-primary-400" /> {lesson.teacher_name}
                  </p>
                </div>
                
              </div>
            ))}
          </div>
        </div>
      )}


        {/* Filters Section */}
        {showFilters && (
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 animate-in slide-in-from-top-2 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subject Filter */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">المادة العلمية</label>
                <select
                  value={selectedSubject}
                  onChange={(e) => {
                    handleFilterChange('subject', e.target.value);
                    setSelectedCourse(''); // Reset course when subject changes
                  }}
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700"
                >
                  <option value="">جميع المواد</option>
                  {filteredSubjects?.map(subject => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
                </select>
              </div>

              {/* Course Filter */}
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">الكورس</label>
                <select
                  value={selectedCourse}
                  onChange={(e) => handleFilterChange('course', e.target.value)}
                  className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700"
                >
                  <option value="">جميع الكورسات</option>
                  {allCourses?.map(course => (
                    <option key={course.id} value={course.id}>{course.title}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters Button */}
            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setSelectedSubject('');
                  setSelectedCourse('');
                }}
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl font-black text-sm transition-all"
              >
                مسح الفلاتر
              </button>
            </div>
          </div>
        )}

        {/* Main Lessons Grid */}
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-2">
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3 tracking-tight">
              <BookOpen className="text-primary-600" />
              جميع المحتويات والدروس
            </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white border border-gray-100 px-4 py-2 rounded-2xl text-[10px] font-black text-gray-600 uppercase tracking-widest shadow-sm hover:bg-gray-50 transition-all flex items-center gap-2"
            >
              <Filter size={14} />
              فلترة
            </button>
            <div className="bg-white border border-gray-100 px-5 py-2 rounded-full text-[10px] font-black text-gray-400 uppercase tracking-widest shadow-sm">
              إجمالي الدروس: {searchedLessons?.length || lessons?.length || 0} دروس
            </div>
          </div>
        </div>

        {(searchedLessons?.length ?? 0) === 0 ? (
          <div className="bg-white rounded-[3rem] shadow-sm p-20 text-center border border-dashed border-gray-200 animate-in fade-in duration-700">
            <div className="bg-gray-50 w-24 h-24 rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-inner">
              <BookOpen className="w-12 h-12 text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد دروس حالياً</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium">
              {hookSearchTerm ? 'نعتذر، لم نجد أي دروس تطابق بحثك الحالي، جرب استخدام كلمات أخرى.' : 'لم تشترك في أي دروس بعد، استكشف قائمة الكورسات المتاحة وابدأ رحلتك.'}
            </p>
            {!hookSearchTerm && (
              <button 
                onClick={() => navigate('/student/browse')} 
                className="mt-8 bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center gap-2 mx-auto"
              >
                استكشف الكورسات <ArrowRight size={20} className="rotate-180" />
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {currentLessons.map((lesson) => (
              <StudentLessonCard
                key={`lesson-card-${lesson.id}`}
                lesson={lesson}
                onView={(id) => handleViewLesson(id)}
                onRate={(lessonId, rating) => handleRateLesson(lessonId, rating)}
              />
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              السابق
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={`w-10 h-10 rounded-xl font-medium transition-all ${
                    currentPage === index + 1
                      ? 'bg-primary-600 text-white'
                      : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              التالي
            </button>
          </div>
        )}
      </div>

      {/* Rating Modal */}
      {ratingModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[3rem] shadow-2xl p-10 max-w-md w-full relative overflow-hidden transition-all transform scale-100">
            <button 
              onClick={() => setRatingModal({ isOpen: false, lessonId: null, currentRating: 0 })}
              className="absolute top-8 left-8 text-gray-300 hover:text-red-500 transition-colors bg-gray-50 p-2 rounded-xl"
            >
              <X size={20} />
            </button>

            <div className="text-center space-y-6">
              <div className="bg-yellow-50 w-24 h-24 rounded-[2rem] flex items-center justify-center mx-auto mb-4 border-2 border-yellow-100 shadow-inner group">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 group-hover:scale-110 transition-transform duration-500" />
              </div>
              <div className="space-y-2">
                <h3 className="text-2xl font-black text-gray-900 tracking-tight">ما هو تقييمك للمحتوى؟</h3>
                <p className="text-gray-400 font-medium leading-relaxed">رأيك الشخصي يساعدنا في تطوير جودة الدروس واختيار أفضل المدرسين لك.</p>
              </div>
              
              <div className="py-6 flex justify-center scale-125">
                <StarRating
                  rating={ratingModal.currentRating}
                  onRate={(rating) => setRatingModal(prev => ({ ...prev, currentRating: rating }))}
                  size="large"
                />
              </div>

              {/* Review Field */}
              <div className="mb-8">
                <label className="block text-sm font-bold text-gray-700 mb-2">مراجعة (اختياري)</label>
                <textarea
                  value={ratingModal.review}
                  onChange={(e) => setRatingModal(prev => ({ ...prev, review: e.target.value }))}
                  placeholder="اكتب انطباعك عن هذا الدرس..."
                  className="w-full p-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  rows="4"
                  maxLength="1000"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl mb-8">
                <p className="text-xs font-bold text-gray-500">
                  {ratingModal.currentRating === 0 ? 'يرجى اختيار عدد النجوم للتقييم' : `لقد اخترت ${toEnglishNumbers(ratingModal.currentRating)} من 5 نجوم`}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setRatingModal({ isOpen: false, lessonId: null, currentRating: 0 })}
                  className="px-6 py-4 border border-gray-100 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"
                >
                  تجاهل
                </button>
                <button
                  onClick={submitRating}
                  disabled={ratingModal.currentRating === 0 || rateLessonMutation.isLoading}
                  className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {rateLessonMutation.isLoading ? <LoadingSpinner size="small" /> : <CheckCircle2 size={18} />}
                  حفظ التقييم
                </button>
              </div>
            </div>
            <div className="absolute bottom-[-20%] right-[-10%] w-40 h-40 bg-yellow-500/5 rounded-full blur-3xl"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lessons;